import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AppraisalTemplateSectionDto,
  AppraisalTemplateSectionCreateDto
} from '../models';

@Injectable()
export class AppraisalTemplateSectionService {
  private readonly API_BASE = `${environment.BASE_URL}/appraisal-template-sections`;

  constructor(private http: HttpClient) {}

  /**
   * Get all sections for a template
   * @param templateId Template ID
   * @returns Observable of section list
   */
  getByTemplateId(
    templateId: number
  ): Observable<AppraisalTemplateSectionDto[]> {
    if (!templateId || templateId <= 0) {
      return throwError(() => new Error('Invalid template ID'));
    }

    return this.http
      .get<
        AppraisalTemplateSectionDto[]
      >(`${this.API_BASE}/template/${templateId}`)
      .pipe(
        map((result: any) => result?.data || result || []),
        catchError(this.handleError)
      );
  }

  /**
   * Get a specific section by ID
   * @param id Section ID
   * @returns Observable of section details
   */
  getById(id: number): Observable<AppraisalTemplateSectionDto> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid section ID'));
    }

    return this.http
      .get<AppraisalTemplateSectionDto>(`${this.API_BASE}/${id}`)
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Create a new section
   * @param templateId Template ID
   * @param section Section data
   * @returns Observable of created section
   */
  create(
    templateId: number,
    section: AppraisalTemplateSectionCreateDto
  ): Observable<AppraisalTemplateSectionDto> {
    if (!templateId || templateId <= 0) {
      return throwError(() => new Error('Invalid template ID'));
    }

    if (!section) {
      return throwError(() => new Error('Section data is required'));
    }

    if (!section.Title || section.Title.trim() === '') {
      return throwError(() => new Error('Section title is required'));
    }

    return this.http
      .post<AppraisalTemplateSectionDto>(
        `${this.API_BASE}/template/${templateId}`,
        section
      )
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Update an existing section
   * @param id Section ID
   * @param section Updated section data
   * @returns Observable of updated section
   */
  update(
    id: number,
    section: AppraisalTemplateSectionCreateDto
  ): Observable<AppraisalTemplateSectionDto> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid section ID'));
    }

    if (!section) {
      return throwError(() => new Error('Section data is required'));
    }

    if (!section.Title || section.Title.trim() === '') {
      return throwError(() => new Error('Section title is required'));
    }

    return this.http
      .put<AppraisalTemplateSectionDto>(`${this.API_BASE}/${id}`, section)
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Delete a section
   * @param id Section ID
   * @returns Observable of deletion result
   */
  delete(id: number): Observable<any> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid section ID'));
    }

    return this.http.delete(`${this.API_BASE}/${id}`).pipe(
      map((result: any) => result),
      catchError(this.handleError)
    );
  }

  /**
   * Update section sort order
   * @param templateId Template ID
   * @param sectionOrders Array of section IDs in new order
   * @returns Observable of update result
   */
  updateSortOrder(
    templateId: number,
    sectionOrders: { id: number; sortOrder: number }[]
  ): Observable<any> {
    if (!templateId || templateId <= 0) {
      return throwError(() => new Error('Invalid template ID'));
    }

    if (!sectionOrders || sectionOrders.length === 0) {
      return throwError(() => new Error('Section orders are required'));
    }

    return this.http
      .put(`${this.API_BASE}/template/${templateId}/reorder`, { sectionOrders })
      .pipe(
        map((result: any) => result),
        catchError(this.handleError)
      );
  }

  /**
   * Duplicate a section
   * @param id Section ID to duplicate
   * @returns Observable of duplicated section
   */
  duplicate(id: number): Observable<AppraisalTemplateSectionDto> {
    if (!id || id <= 0) {
      return throwError(() => new Error('Invalid section ID'));
    }

    return this.http
      .post<AppraisalTemplateSectionDto>(`${this.API_BASE}/${id}/duplicate`, {})
      .pipe(
        map((result: any) => result?.data || result),
        catchError(this.handleError)
      );
  }

  /**
   * Handle HTTP errors
   * @param error HTTP error response
   * @returns Observable error
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client Error: ${error.error.message}`;
    } else {
      if (error.status === 0) {
        errorMessage =
          'Unable to connect to the server. Please check your connection.';
      } else if (error.status === 400) {
        errorMessage = error.error?.message || 'Invalid request data';
      } else if (error.status === 404) {
        errorMessage = 'Section not found.';
      } else if (error.status === 409) {
        errorMessage = error.error?.message || 'A conflict occurred.';
      } else if (error.status === 422) {
        errorMessage =
          error.error?.message || 'Validation failed. Please check your input.';
      } else if (error.status >= 500) {
        errorMessage = 'A server error occurred. Please try again later.';
      } else {
        errorMessage = error.error?.message || `Server Error: ${error.status}`;
      }
    }

    console.error(
      'AppraisalTemplateSectionService Error:',
      errorMessage,
      error
    );
    return throwError(() => new Error(errorMessage));
  }
}
